import { useCallback, useMemo, useRef, useState } from 'react';

import { Icon } from '@/components/icons/Icon';
import type { IconName } from '@/components/icons/Icon';
import { Markdown } from '@/components/ui/Markdown';
import { countWords } from '@/lib/prose';
import { cn } from '@/lib/cn';

/**
 * The writing surface for pages and blog articles.
 *
 * ── WHY MARKDOWN AND NOT A WYSIWYG ──────────────────────────────────────────
 *
 * A rich-text editor stores HTML. HTML pasted out of Word, out of a browser, or
 * typed by somebody who found the source view, is untrusted input that then has
 * to be sanitised correctly for ever — and a sanitiser that is wrong once is an
 * XSS hole on a page every buyer loads. The whole product has exactly zero
 * `dangerouslySetInnerHTML` calls, and that is worth more than italics being
 * italic while you type.
 *
 * So: Markdown in a textarea, rendered to React nodes by `Markdown`. There is
 * no code path from what a writer types to live markup in a reader's browser.
 *
 * What a writer loses is seeing the formatting in place. What this gives back:
 *
 *   • a toolbar, so nobody has to remember the syntax
 *   • the real renderer as a live preview — not an approximation of it, the
 *     exact component the public page uses
 *   • keyboard shortcuts that match every other editor (⌘B, ⌘I, ⌘K)
 *   • selection-aware toggles: pressing Bold on bold text unbolds it
 *   • pictures uploaded by drag, paste or button, inserted at the cursor
 *   • a word count and the same reading-time arithmetic the server does
 *   • undo that still works, because every edit goes through the textarea
 *
 * ── THAT LAST ONE IS NOT A DETAIL ───────────────────────────────────────────
 *
 * The obvious way to write a toolbar is to set `value` from React. Do that and
 * ⌘Z stops working, because the browser's undo stack only knows about edits the
 * user made. `document.execCommand('insertText')` is deprecated and still the
 * only way to put text into a textarea as though it were typed, which keeps
 * native undo, native redo and the spellchecker intact. There is a fallback for
 * the day it is removed.
 */

type Tool = {
  name: string;
  icon: IconName;
  hint: string;
  key?: string;
  /**
   * Whether this tool works on whole lines.
   *
   * Declared on the TOOL rather than returned in the Edit, because the
   * selection has to be widened to the line bounds BEFORE `apply` sees it.
   * Returning it afterwards was a bug: the replacement was built from the
   * cursor-to-cursor selection and then written over the whole line, so
   * everything before the cursor on that line was eaten.
   */
  block?: boolean;
  apply: (state: Selection) => Edit;
};

/** What the textarea holds and what is selected in it. */
type Selection = { value: string; start: number; end: number; selected: string };

/** What a tool wants done: text to put in, and where to leave the cursor. */
type Edit = { text: string; select?: [number, number] };

export function MarkdownEditor({
  value,
  onChange,
  onUploadImage,
  placeholder,
  minRows = 18,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Returns the URL to insert, or null if the upload failed. */
  onUploadImage?: (file: File) => Promise<string | null>;
  placeholder?: string;
  minRows?: number;
  id?: string;
}) {
  const area = useRef<HTMLTextAreaElement>(null);
  const [view, setView] = useState<'write' | 'preview'>('write');
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const stats = useMemo(() => countWords(value), [value]);

  /**
   * Replace the selection as though it had been typed.
   *
   * `execCommand` is deprecated but keeps the browser's own undo stack, which
   * `setRangeText` and a React-controlled value both destroy. The fallback runs
   * only if it is gone, and loses undo for that one edit rather than silently
   * doing nothing.
   */
  const insert = useCallback(
    (edit: Edit) => {
      const node = area.current;

      if (!node) return;

      node.focus();

      const inserted = document.execCommand('insertText', false, edit.text);

      if (!inserted) {
        const { selectionStart: from, selectionEnd: to } = node;
        node.setRangeText(edit.text, from, to, 'end');
        onChange(node.value);
      }

      if (edit.select) {
        const base = node.selectionStart - edit.text.length;
        node.setSelectionRange(base + edit.select[0], base + edit.select[1]);
      }

      // `insertText` fires `input`, so React hears it. The explicit call covers
      // the fallback path and costs nothing when it is a no-op.
      onChange(node.value);
    },
    [onChange],
  );

  const run = useCallback(
    (tool: Tool) => {
      const node = area.current;

      if (!node) return;

      let start = node.selectionStart;
      let end = node.selectionEnd;

      // A block tool (heading, list, quote) owns whole lines. Pressing
      // "Heading" halfway through a sentence must turn that LINE into a
      // heading rather than bury `## ` in the middle of a paragraph — so the
      // selection is widened to the line bounds BEFORE the tool sees it, and
      // the tool is handed exactly the lines it is going to replace.
      if (tool.block) {
        start = node.value.lastIndexOf('\n', start - 1) + 1;

        const lineEnd = node.value.indexOf('\n', end);
        end = lineEnd === -1 ? node.value.length : lineEnd;

        node.setSelectionRange(start, end);
      }

      const edit = tool.apply({
        value: node.value,
        start,
        end,
        selected: node.value.slice(start, end),
      });

      insert(edit);
    },
    [insert],
  );

  const upload = useCallback(
    async (files: FileList | File[] | null) => {
      const pictures = Array.from(files ?? []).filter((file) => file.type.startsWith('image/'));

      if (!onUploadImage || pictures.length === 0) return;

      setBusy(true);
      setUploadError(null);

      try {
        for (const file of pictures) {
          const url = await onUploadImage(file);

          if (!url) {
            setUploadError('That picture could not be uploaded. Try a JPEG or PNG under 4MB.');
            continue;
          }

          // The alt text is the filename, which is a poor caption and a much
          // better starting point than an empty one: a writer who sees
          // "ward-round.jpg" under a picture will rewrite it, and one who sees
          // nothing will not notice it is missing.
          insert({ text: `\n\n![${altFrom(file.name)}](${url})\n\n` });
        }
      } finally {
        setBusy(false);
      }
    },
    [insert, onUploadImage],
  );

  // ⌘B / ⌘I / ⌘K, and ⌘⇧P for the preview. Registered on the textarea rather
  // than the window so the shortcuts cannot steal keys from the rest of the
  // console.
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(event.metaKey || event.ctrlKey)) {
      if (event.key === 'Tab') return; // Tab still moves focus. It is a form.

      return;
    }

    if (event.shiftKey && event.key.toLowerCase() === 'p') {
      event.preventDefault();
      setView((current) => (current === 'write' ? 'preview' : 'write'));

      return;
    }

    const tool = TOOLS.find((one) => one.key && one.key === event.key.toLowerCase());

    if (tool) {
      event.preventDefault();
      run(tool);
    }
  };

  return (
    <div
      className={cn(
        'rounded-card border bg-white',
        dragging ? 'border-blue ring-2 ring-blue/20' : 'border-line',
      )}
      onDragOver={(event) => {
        if (!onUploadImage) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (!onUploadImage) return;
        event.preventDefault();
        setDragging(false);
        void upload(event.dataTransfer.files);
      }}
    >
      {/* ── The toolbar ──────────────────────────────────────────────────
          Wraps rather than scrolls. A horizontally scrolling toolbar on a
          phone hides half its own buttons behind a gesture nobody discovers,
          and these are 36px targets either way. */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-1.5 py-1.5">
        {TOOLS.map((tool) =>
          // A `Math.random()` key here re-created every separator on every
          // keystroke, which is a remount per character typed. The names are
          // distinct in TOOLS for exactly this reason.
          tool.name.startsWith('|') ? (
            <span key={tool.name} aria-hidden className="mx-1 h-5 w-px bg-line" />
          ) : (
            <button
              key={tool.name}
              type="button"
              onClick={() => run(tool)}
              title={tool.hint}
              aria-label={tool.hint}
              data-testid={`md-${tool.name}`}
              className="grid h-9 w-9 place-items-center rounded-btn text-muted hover:bg-shell hover:text-ink"
            >
              <Icon name={tool.icon} size={15} />
            </button>
          ),
        )}

        {onUploadImage ? (
          <label
            title="Insert a picture — or drag one in, or paste it"
            className="grid h-9 w-9 cursor-pointer place-items-center rounded-btn text-muted hover:bg-shell hover:text-ink"
          >
            <Icon name={busy ? 'clock' : 'image'} size={15} />
            <input
              type="file"
              accept="image/*"
              multiple
              data-testid="md-image"
              onChange={(event) => {
                void upload(event.target.files);
                event.target.value = '';
              }}
              className="sr-only"
            />
          </label>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setView(view === 'write' ? 'preview' : 'write')}
            aria-pressed={view === 'preview'}
            data-testid="md-preview"
            className={cn(
              'flex h-9 items-center gap-1.5 rounded-btn px-2.5 text-[12px] font-semibold',
              view === 'preview' ? 'bg-blue text-white' : 'text-muted hover:bg-shell',
            )}
          >
            <Icon name="eye" size={14} />
            Preview
          </button>
        </div>
      </div>

      {view === 'write' ? (
        <textarea
          ref={area}
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData.files);

            if (onUploadImage && files.some((file) => file.type.startsWith('image/'))) {
              event.preventDefault();
              void upload(files);
            }
          }}
          rows={minRows}
          placeholder={placeholder}
          spellCheck
          // `font-mono` on purpose. Markdown is written, not drawn: a fixed
          // pitch makes a stray asterisk or an unclosed bracket visible, and
          // keeps indented list items lined up.
          className="block w-full resize-y border-0 bg-transparent p-3.5 font-mono text-[13.5px] leading-[1.7] text-ink outline-none placeholder:text-faint"
        />
      ) : (
        <div className="p-3.5" data-testid="md-preview-pane">
          {value.trim() ? (
            // The real renderer, at the real reading scale. A preview that
            // merely approximated the page would be a second thing to keep in
            // step, and the first place a difference would show is in public.
            <Markdown source={value} variant="article" className="max-w-[68ch]" />
          ) : (
            <p className="m-0 py-10 text-center text-[13px] text-faint">
              Nothing to preview yet.
            </p>
          )}
        </div>
      )}

      {/* ── The status line ──────────────────────────────────────────────
          The same 220-words-a-minute arithmetic the server uses, so the figure
          a writer sees while writing is the figure readers get. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-3 py-2 text-[11px] text-faint">
        <span>
          <strong className="font-semibold text-muted">{stats.words.toLocaleString()}</strong> words
        </span>
        <span>{stats.minutes} min read</span>
        <span className="hidden sm:inline">
          {stats.characters.toLocaleString()} characters
        </span>

        {uploadError ? (
          <span className="text-danger" role="status">
            {uploadError}
          </span>
        ) : null}

        <span className="ml-auto hidden md:inline">
          **bold** · *italic* · ## heading · &gt; quote · ⌘B ⌘I ⌘K · ⌘⇧P preview
        </span>
      </div>
    </div>
  );
}

/**
 * Wrap the selection, or unwrap it if it is already wrapped.
 *
 * Pressing Bold on bold text has to unbold it. The alternative is `****word****`
 * the second time somebody presses the button, which is the single most
 * irritating bug a toolbar can have.
 */
function toggleWrap(marker: string) {
  return ({ value, start, end, selected }: Selection): Edit => {
    const before = value.slice(Math.max(0, start - marker.length), start);
    const after = value.slice(end, end + marker.length);

    if (before === marker && after === marker) {
      // The markers are outside the selection: widen, drop them, keep the words.
      return { text: selected, select: [0, selected.length] };
    }

    if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length > marker.length * 2) {
      const inner = selected.slice(marker.length, -marker.length);

      return { text: inner, select: [0, inner.length] };
    }

    if (!selected) {
      // Nothing selected: leave the cursor between the markers, ready to type.
      return { text: `${marker}${marker}`, select: [marker.length, marker.length] };
    }

    return {
      text: `${marker}${selected}${marker}`,
      select: [marker.length, marker.length + selected.length],
    };
  };
}

/** Put a prefix on every line of the selection, or take it off again. */
function togglePrefix(prefix: string, numbered = false) {
  return ({ selected }: Selection): Edit => {
    const lines = (selected || '').split('\n');
    const stripped = numbered ? /^\s*\d+\.\s+/ : new RegExp(`^\\s*${escapeRegExp(prefix)}`);

    // `every` over nothing but blank lines is vacuously TRUE, which made
    // this read "already a list" on an empty line and insert nothing — a
    // dead button, on exactly the line somebody starts a list on.
    const hasText = lines.some((line) => line.trim() !== '');
    const on = hasText && lines.every((line) => line.trim() === '' || stripped.test(line));

    const next = lines
      .map((line, index) => {
        if (line.trim() === '') return line;
        if (on) return line.replace(stripped, '');

        return `${numbered ? `${index + 1}. ` : prefix}${line}`;
      })
      .join('\n');

    // An empty selection gets the bare prefix, cursor after it, ready to type.
    return hasText
      ? { text: next, select: [0, next.length] }
      : { text: prefix, select: [prefix.length, prefix.length] };
  };
}

/**
 * Make these lines a heading of this level, or plain text if they already are.
 *
 * Any existing marker is REPLACED, not added to. `### ## text` is what the
 * naive version produced the first time somebody pressed H3 on an H2 line, and
 * it renders as a sub-heading whose text begins with two hashes.
 */
function toggleHeading(level: 2 | 3) {
  const marker = '#'.repeat(level);

  return ({ selected }: Selection): Edit => {
    const next = (selected || '')
      .split('\n')
      .map((line) => {
        const bare = line.replace(/^\s*#{1,6}\s*/, '');
        const already = new RegExp('^\\s*' + marker + '\\s').test(line);

        return already ? bare : marker + ' ' + bare;
      })
      .join('\n');

    return selected
      ? { text: next, select: [0, next.length] }
      : { text: marker + ' ', select: [marker.length + 1, marker.length + 1] };
  };
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const TOOLS: Tool[] = [
  {
    name: 'bold',
    icon: 'bold',
    hint: 'Bold (⌘B)',
    key: 'b',
    apply: toggleWrap('**'),
  },
  {
    name: 'italic',
    icon: 'italic',
    hint: 'Italic (⌘I)',
    key: 'i',
    apply: toggleWrap('*'),
  },
  {
    name: 'strike',
    icon: 'strikethrough',
    hint: 'Strikethrough',
    apply: toggleWrap('~~'),
  },
  { name: '|1', icon: 'doc', hint: '', apply: () => ({ text: '' }) },
  {
    name: 'h2',
    icon: 'heading',
    hint: 'Section heading',
    block: true,
    apply: toggleHeading(2),
  },
  {
    name: 'h3',
    icon: 'heading-small',
    hint: 'Sub-heading',
    block: true,
    apply: toggleHeading(3),
  },
  { name: '|2', icon: 'doc', hint: '', apply: () => ({ text: '' }) },
  {
    name: 'bullets',
    icon: 'list',
    hint: 'Bulleted list',
    block: true,
    apply: togglePrefix('- '),
  },
  {
    name: 'numbers',
    icon: 'list-ordered',
    hint: 'Numbered list',
    block: true,
    apply: togglePrefix('1. ', true),
  },
  {
    name: 'quote',
    icon: 'quote',
    hint: 'Quote',
    block: true,
    apply: togglePrefix('> '),
  },
  { name: '|3', icon: 'doc', hint: '', apply: () => ({ text: '' }) },
  {
    name: 'link',
    icon: 'link',
    hint: 'Link (⌘K)',
    key: 'k',
    apply: ({ selected }) => {
      // A URL already on the clipboard is the common case, but reading the
      // clipboard needs a permission prompt that would interrupt typing. So:
      // the label is filled from the selection and the cursor lands in the
      // brackets, ready to paste.
      const label = selected || 'link text';

      return {
        text: `[${label}](https://)`,
        select: [label.length + 3, label.length + 11],
      };
    },
  },
  {
    name: 'code',
    icon: 'code',
    hint: 'Code',
    apply: toggleWrap('`'),
  },
  {
    name: 'rule',
    icon: 'minus',
    hint: 'Divider',
    apply: () => ({ text: '\n\n---\n\n' }),
  },
];

/** A filename, made into something a person would write. */
function altFrom(name: string): string {
  const base = name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim();

  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Picture';
}
